import React, { useState, useRef } from 'react';
import './MetadataExtractor.css';
import {
  ArrowLeft,
  ChevronDown,
  Sun,
  Moon,
  Menu,
  User,
  X,
  Upload,
  FileText,
  Check,
  Loader,
  Download,
  ChevronLeft,
  ChevronRight,
  Database,
  Copy,
  FileDown
} from 'lucide-react';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://webapp-python-nlng-bots-api-cuasbrg3h5cyewgm.westeurope-01.azurewebsites.net';

const MetadataExtractorRevamped = ({ onBack, onNavigate, lightMode: initialLightMode = true }) => {
  // ============================================
  // STATE MANAGEMENT
  // ============================================
  const [lightMode, setLightMode] = useState(initialLightMode);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  
  // Upload states
  const [currentState, setCurrentState] = useState('welcome'); // 'welcome', 'uploading', 'uploaded', 'results'
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [uploadingFiles, setUploadingFiles] = useState([]);
  const [currentDocumentIndex, setCurrentDocumentIndex] = useState(0);
  const [isExtracting, setIsExtracting] = useState(false);
  const [duplicateFiles, setDuplicateFiles] = useState([]);
  const [copiedField, setCopiedField] = useState(null);
  const [extractionError, setExtractionError] = useState(null);

  const fileInputRef = useRef(null);
  const scrollContainerRef = useRef(null);

  // ============================================
  // METADATA STATE
  // ============================================
  const [extractedMetadata, setExtractedMetadata] = useState([]);

  // ============================================
  // HANDLER FUNCTIONS
  // ============================================

  const checkForDuplicates = (newFiles) => {
    const existingFileNames = new Set([
      ...uploadedFiles.map(f => f.name),
      ...uploadingFiles.map(f => f.name)
    ]);
    
    const duplicates = [];
    const uniqueFiles = [];
    
    newFiles.forEach(file => {
      if (existingFileNames.has(file.name)) {
        duplicates.push(file);
      } else {
        uniqueFiles.push(file);
      }
    });
    
    return { uniqueFiles, duplicates };
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    
    const allowedTypes = ['.pdf', '.txt', '.doc', '.docx'];
    const validFiles = files.filter(file => {
      const ext = '.' + file.name.split('.').pop().toLowerCase();
      return allowedTypes.includes(ext);
    });

    if (validFiles.length === 0) return;

    const newFiles = validFiles.map(file => ({
      id: `${Date.now()}-${Math.random()}-${file.name}`,
      name: file.name,
      size: formatFileSize(file.size),
      type: file.name.split('.').pop().toUpperCase(),
      status: 'pending',
      file: file,
      uploadDate: new Date().toISOString()
    }));

    // Check for duplicates
    const { uniqueFiles, duplicates } = checkForDuplicates(newFiles);
    
    if (duplicates.length > 0) {
      setDuplicateFiles(duplicates);
      setTimeout(() => {
        setDuplicateFiles([]);
      }, 3000);
    }

    if (uniqueFiles.length === 0) {
      e.target.value = '';
      return;
    }

    setUploadingFiles(prev => [...prev, ...uniqueFiles]);
    setCurrentState('uploading');
    simulateUpload(uniqueFiles);
    
    // Reset file input
    e.target.value = '';
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(0) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const simulateUpload = (files) => {
    // Immediately stage files — no fake progress delay needed
    setUploadProgress(0);
    let progress = 0;
    const interval = setInterval(() => {
      progress += 20;
      setUploadProgress(Math.min(progress, 100));
      if (progress >= 100) {
        clearInterval(interval);
        const uploadedFilesList = files.map(file => ({ ...file, status: 'uploaded' }));
        setUploadedFiles(prev => {
          const existingNames = new Set(prev.map(f => f.name));
          const trulyNewFiles = uploadedFilesList.filter(f => !existingNames.has(f.name));
          return [...prev, ...trulyNewFiles];
        });
        setUploadingFiles([]);
        setTimeout(() => setCurrentState('uploaded'), 300);
      }
    }, 80);
  };

  const handleExtractMetadata = async () => {
    setIsExtracting(true);
    setExtractionError(null);

    const extraction_id = `ext-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    const formData = new FormData();
    const user_id = localStorage.getItem('userId') || 'unknown';
    formData.append('user_id', user_id);

    const filesWithContent = uploadedFiles.filter(f => f.file);
    if (filesWithContent.length === 0) {
      setIsExtracting(false);
      setExtractionError('No valid files to extract from.');
      return;
    }

    for (const fileObj of filesWithContent) {
      formData.append('files', fileObj.file, fileObj.name);
    }

    try {
      const res = await fetch(
        `${API_BASE_URL}/v1/api/tea/extract-metadata/stream?extraction_id=${extraction_id}`,
        { method: 'POST', body: formData }
      );

      if (!res.ok) {
        const errText = await res.text().catch(() => '');
        throw new Error(`Extraction failed: ${res.status} ${errText}`);
      }

      // Read streaming / SSE body
      let text = '';
      if (res.body && typeof res.body.getReader === 'function') {
        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let done = false;
        while (!done) {
          const { value, done: d } = await reader.read();
          if (value) text += decoder.decode(value, { stream: true });
          done = d;
        }
        text += decoder.decode();
      } else {
        text = await res.text();
      }

      // Parse JSON / SSE / NDJSON
      let parsed = null;
      const allLines = (text || '').split(/\r?\n/).map(l => l.trim()).filter(Boolean);

      try {
        parsed = JSON.parse(text);
      } catch {
        const dataLines = [];
        for (const ln of allLines) {
          if (ln.startsWith('data: ')) {
            try { dataLines.push(JSON.parse(ln.substring(6))); } catch { /* ignore */ }
          }
        }
        if (dataLines.length > 0) {
          parsed = dataLines.length === 1 ? dataLines[0] : dataLines;
        } else if (allLines.length > 0) {
          const last = allLines[allLines.length - 1];
          try { parsed = JSON.parse(last); } catch {
            const arr = [];
            for (const ln of allLines) { try { arr.push(JSON.parse(ln)); } catch { /* ignore */ } }
            if (arr.length > 0) parsed = arr;
          }
        }
      }

      // Normalise a single record into { id, documentName, metadata: [{label, value}] }
      const fileNames = uploadedFiles.map(f => f.name);
      const normalizeRecord = (record, idx) => {
        let flatMeta = {};

        if (record && typeof record === 'object') {
          if (record.metadata?.metadata && typeof record.metadata.metadata === 'object') {
            flatMeta = { ...record.metadata.metadata };
          } else if (record.Metadata && typeof record.Metadata === 'object') {
            flatMeta = { ...record.Metadata };
          } else if (record.metadata && typeof record.metadata === 'object') {
            flatMeta = { ...record.metadata };
          }
        }

        const docName = (record && (record.document_name || record.documentName)) || fileNames[idx] || `Document ${idx + 1}`;
        const metaId = (record && (record.metadata_id || record.metadataId)) || extraction_id;

        // Include any top-level primitive fields too
        if (record && typeof record === 'object') {
          Object.keys(record).forEach(k => {
            if (!['metadata', 'Metadata', 'document_name', 'documentName', 'metadata_id', 'metadataId', 'document_path', 'documentPath', 'raw'].includes(k)) {
              const v = record[k];
              if (v != null && typeof v !== 'object') flatMeta[k] = v;
            }
          });
        }
        delete flatMeta.raw;

        const metadataArray = Object.entries(flatMeta).map(([key, val]) => ({
          label: key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) + ':',
          value: String(val ?? ''),
        }));

        return {
          id: metaId,
          documentName: docName,
          metadata: metadataArray.length > 0 ? metadataArray : [
            { label: 'Document Name:', value: docName },
            { label: 'Extraction ID:', value: metaId },
          ],
        };
      };

      let records = [];
      if (parsed == null) {
        records = uploadedFiles.map((f, i) => normalizeRecord(null, i));
      } else if (Array.isArray(parsed)) {
        records = parsed.map((rec, i) => normalizeRecord(rec.extraction_metadata || rec, i));
      } else if (parsed.extraction_metadata) {
        records = [normalizeRecord(parsed.extraction_metadata, 0)];
      } else {
        records = [normalizeRecord(parsed, 0)];
      }

      setExtractedMetadata(records);
      setCurrentDocumentIndex(0);
      setCurrentState('results');
    } catch (err) {
      console.error('Extraction failed:', err);
      let msg = 'Extraction failed. Please try again.';
      if (err.message?.includes('CORS') || err.message?.includes('Failed to fetch')) {
        msg = 'Network error: Unable to connect to TEA extraction API. Please check your connection.';
      } else if (err.message) {
        msg = err.message;
      }
      setExtractionError(msg);
    } finally {
      setIsExtracting(false);
    }
  };

  const removeFile = (id) => {
    setUploadedFiles(prev => {
      const newFiles = prev.filter(file => file.id !== id);
      if (newFiles.length === 0) {
        setCurrentState('welcome');
        setUploadProgress(0);
      }
      return newFiles;
    });
  };

  const clearAllFiles = () => {
    setUploadedFiles([]);
    setUploadingFiles([]);
    setCurrentState('welcome');
    setUploadProgress(0);
    setDuplicateFiles([]);
  };

  const goToPrevDocument = () => {
    setCurrentDocumentIndex(prev => Math.max(0, prev - 1));
  };

  const goToNextDocument = () => {
    setCurrentDocumentIndex(prev => Math.min(extractedMetadata.length - 1, prev + 1));
  };

  const triggerFileUpload = () => {
    fileInputRef.current.click();
  };

  const getTotalMetadataCount = () => {
    return extractedMetadata.reduce((acc, doc) => acc + doc.metadata.length, 0);
  };

  // Copy to clipboard function
  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedField(label);
      setTimeout(() => {
        setCopiedField(null);
      }, 2000);
    });
  };

  // Save current document as CSV
  const saveCurrentAsCSV = () => {
    if (extractedMetadata.length === 0) return;
    
    const currentDoc = extractedMetadata[currentDocumentIndex];
    let csvContent = "Field,Value\n";
    currentDoc.metadata.forEach(item => {
      csvContent += `"${item.label}","${item.value}"\n`;
    });
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${currentDoc.documentName.replace(/\.[^/.]+$/, "").replace(/\s+/g, '_')}_metadata.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Save all documents as a single collated CSV
  // Rows = attribute labels, Columns = file names
  const saveAllAsCollatedCSV = () => {
    if (extractedMetadata.length === 0) return;

    // Collect all unique labels (in first-seen order across all docs)
    const allLabels = [];
    const labelSet = new Set();
    extractedMetadata.forEach(doc => {
      doc.metadata.forEach(item => {
        if (!labelSet.has(item.label)) {
          labelSet.add(item.label);
          allLabels.push(item.label);
        }
      });
    });

    // Build lookup: doc index -> label -> value
    const valueLookup = extractedMetadata.map(doc => {
      const map = {};
      doc.metadata.forEach(item => { map[item.label] = item.value; });
      return map;
    });

    // Header row: "Attribute", "File1", "File2", ...
    const escapeCsv = (s) => `"${String(s ?? '').replace(/"/g, '""')}"`;
    const fileHeaders = extractedMetadata.map(doc => escapeCsv(doc.documentName));
    let csvContent = `"Attribute",${fileHeaders.join(',')}
`;

    // Data rows
    allLabels.forEach(label => {
      const values = valueLookup.map(lookup => escapeCsv(lookup[label] ?? ''));
      csvContent += `${escapeCsv(label)},${values.join(',')}
`;
    });

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `all_documents_metadata_collated.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Generate HTML page for current document
  const generateHTMLPage = (doc) => {
    return `
      <!DOCTYPE html>
      <html lang="en">
      <head>
        <meta charset="UTF-8">
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <title>${doc.documentName} - Metadata</title>
        <style>
          body {
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, sans-serif;
            margin: 0;
            padding: 20px;
            background-color: #f5f5f5;
          }
          .container {
            max-width: 800px;
            margin: 0 auto;
            background-color: white;
            border-radius: 12px;
            box-shadow: 0 4px 6px rgba(0,0,0,0.1);
            padding: 30px;
          }
          h1 {
            color: #333;
            border-bottom: 2px solid #eee;
            padding-bottom: 10px;
            margin-bottom: 20px;
            font-size: 24px;
          }
          .metadata-grid {
            display: grid;
            grid-template-columns: auto 1fr;
            gap: 12px 20px;
          }
          .label {
            font-weight: 600;
            color: #666;
            white-space: nowrap;
          }
          .value {
            color: #333;
            word-break: break-word;
          }
          .footer {
            margin-top: 30px;
            text-align: center;
            color: #999;
            font-size: 12px;
          }
        </style>
      </head>
      <body>
        <div class="container">
          <h1>${doc.documentName} - Metadata</h1>
          <div class="metadata-grid">
            ${doc.metadata.map(item => `
              <div class="label">${item.label}</div>
              <div class="value">${item.value}</div>
            `).join('')}
          </div>
          <div class="footer">
            Generated on ${new Date().toLocaleString()}
          </div>
        </div>
      </body>
      </html>
    `;
  };

  // Generate HTML page for all documents
  const generateAllHTMLPage = () => {
    return `
      <!DOCTYPE html>
      <html lang="en">
      <head>
        <meta charset="UTF-8">
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <title>All Documents - Metadata Export</title>
        <style>
          body {
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, sans-serif;
            margin: 0;
            padding: 20px;
            background-color: #f5f5f5;
          }
          .container {
            max-width: 900px;
            margin: 0 auto;
          }
          .document-card {
            background-color: white;
            border-radius: 12px;
            box-shadow: 0 4px 6px rgba(0,0,0,0.1);
            padding: 25px;
            margin-bottom: 20px;
          }
          h1 {
            color: #333;
            margin-bottom: 20px;
          }
          h2 {
            color: #444;
            border-bottom: 2px solid #eee;
            padding-bottom: 8px;
            margin-bottom: 15px;
            font-size: 20px;
          }
          .metadata-grid {
            display: grid;
            grid-template-columns: auto 1fr;
            gap: 10px 20px;
          }
          .label {
            font-weight: 600;
            color: #666;
            white-space: nowrap;
          }
          .value {
            color: #333;
            word-break: break-word;
          }
          .footer {
            margin-top: 30px;
            text-align: center;
            color: #999;
            font-size: 12px;
          }
        </style>
      </head>
      <body>
        <div class="container">
          <h1>Metadata Export - All Documents</h1>
          ${extractedMetadata.map(doc => `
            <div class="document-card">
              <h2>${doc.documentName}</h2>
              <div class="metadata-grid">
                ${doc.metadata.map(item => `
                  <div class="label">${item.label}</div>
                  <div class="value">${item.value}</div>
                `).join('')}
              </div>
            </div>
          `).join('')}
          <div class="footer">
            Generated on ${new Date().toLocaleString()} | Total Documents: ${extractedMetadata.length}
          </div>
        </div>
      </body>
      </html>
    `;
  };

  // Save current document as HTML page
  const saveCurrentAsHTML = () => {
    if (extractedMetadata.length === 0) return;
    
    const currentDoc = extractedMetadata[currentDocumentIndex];
    const htmlContent = generateHTMLPage(currentDoc);
    
    const blob = new Blob([htmlContent], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${currentDoc.documentName.replace(/\.[^/.]+$/, "").replace(/\s+/g, '_')}_metadata.html`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Save all documents as HTML page
  const saveAllAsHTML = () => {
    if (extractedMetadata.length === 0) return;
    
    const htmlContent = generateAllHTMLPage();
    
    const blob = new Blob([htmlContent], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `all_documents_metadata.html`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Get file icon based on type
  const getFileIcon = (fileType) => {
    return <FileText size={20} />;
  };

  // ============================================
  // RENDER FUNCTIONS
  // ============================================
  const renderWelcomeState = () => (
    <div className="metadata-welcome-container">
      <div className="metadata-welcome-content">
        <h1 className="metadata-welcome-title">
          <span className="metadata-gradient-text">Metadata Extractor</span>
        </h1>
        <p className="metadata-welcome-subtitle">Upload documents to extract metadata.</p>
      </div>

      {/* Upload Area */}
      <div className="metadata-upload-section">
        <div
          className="metadata-upload-wrapper"
          onClick={triggerFileUpload}
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => {
            e.preventDefault();
            const files = Array.from(e.dataTransfer.files);
            if (files.length > 0) handleFileSelect({ target: { files } });
          }}
        >
          <div className="metadata-upload-area">
            <div className="metadata-upload-icon-circle">
              <Upload size={28} className="metadata-upload-icon" />
            </div>
            <p className="metadata-upload-title">
              <span className="metadata-upload-link">Click to upload</span>
              <span className="metadata-upload-or"> or drag and drop file here</span>
            </p>
            <p className="metadata-upload-description">PDF, TXT or DOCS (max. 4MB)</p>
          </div>
        </div>
      </div>

      {/* Duplicate Warning */}
      {duplicateFiles.length > 0 && (
        <div className="metadata-duplicate-warning">
          <span className="metadata-warning-icon">⚠️</span>
          <span className="metadata-warning-text">
            {duplicateFiles.length} duplicate file{duplicateFiles.length > 1 ? 's' : ''} skipped: {duplicateFiles.map(f => f.name).join(', ')}
          </span>
        </div>
      )}

      {/* Action Button */}
      <div className="metadata-action-buttons">
        <button className="metadata-extract-btn" onClick={triggerFileUpload}>
          Extract Metadata
        </button>
      </div>
    </div>
  );

  const renderUploadingState = () => (
    <div className="metadata-welcome-container">
      <div className="metadata-progress-container">
        <div className="metadata-progress-header">
          <span className="metadata-progress-percentage">{Math.round(uploadProgress)}%</span>
          <span className="metadata-files-count">
            Uploading {uploadingFiles.length} file{uploadingFiles.length > 1 ? 's' : ''}...
          </span>
        </div>
        
        <div className="metadata-progress-bar">
          <div 
            className="metadata-progress-fill" 
            style={{ width: `${uploadProgress}%` }}
          ></div>
        </div>
        
        {/* Uploading Files List */}
        <div className="metadata-uploading-list">
          {uploadingFiles.map((file) => (
            <div key={file.id} className="metadata-uploading-item">
              <div className="metadata-file-info">
                <div className="metadata-file-icon">
                  {getFileIcon(file.type)}
                </div>
                <div className="metadata-file-details">
                  <span className="metadata-file-name">{file.name}</span>
                  <span className="metadata-file-size">{file.size}</span>
                </div>
              </div>
              <div className="metadata-file-status">
                <Loader size={16} className="spinner" />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Warning Box */}
      <div className="metadata-warning-box">
        <span className="metadata-warning-icon">⚠️</span>
        <span className="metadata-warning-text">
          Metadata extraction is automated. Please verify extracted data for accuracy.
        </span>
      </div>
    </div>
  );

  const renderUploadedState = () => (
    <div className="metadata-welcome-container">
      {/* Success Message */}
      <div className="metadata-success-container">
        <div className="metadata-success-icon">
          <Check size={18} />
        </div>
        <span className="metadata-success-text">
          {uploadedFiles.length} file{uploadedFiles.length > 1 ? 's' : ''} uploaded successfully!
        </span>
      </div>

      {/* Duplicate Warning */}
      {duplicateFiles.length > 0 && (
        <div className="metadata-duplicate-warning">
          <span className="metadata-warning-icon">⚠️</span>
          <span className="metadata-warning-text">
            {duplicateFiles.length} duplicate file{duplicateFiles.length > 1 ? 's' : ''} skipped
          </span>
        </div>
      )}

      {/* Uploaded Documents List */}
      <div className="metadata-uploaded-list">
        <div className="metadata-list-header">
          <h3 className="metadata-list-title">
            Uploaded Documents ({uploadedFiles.length})
          </h3>
          <button className="metadata-clear-all" onClick={clearAllFiles}>
            <X size={14} /> Clear all
          </button>
        </div>

        <div className="metadata-files-scroll">
          {uploadedFiles.map((file) => (
            <div key={file.id} className="metadata-file-item">
              <div className="metadata-file-info">
                <div className="metadata-file-icon">
                  {getFileIcon(file.type)}
                </div>
                <div className="metadata-file-details">
                  <span className="metadata-file-name">{file.name}</span>
                  <span className="metadata-file-size">{file.size}</span>
                </div>
              </div>
              <button 
                className="metadata-remove-file"
                onClick={() => removeFile(file.id)}
              >
                <X size={16} />
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Action Buttons */}
      <div className="metadata-action-buttons">
        <button 
          className="metadata-action-btn"
          onClick={handleExtractMetadata}
          disabled={isExtracting || uploadedFiles.length === 0}
        >
          {isExtracting ? (
            <>
              <Loader size={16} className="spinner" />
              Extracting...
            </>
          ) : (
            <>
              <Database size={16} />
              Extract Metadata ({uploadedFiles.length} file{uploadedFiles.length > 1 ? 's' : ''})
            </>
          )}
        </button>
        <button className="metadata-action-btn outline" onClick={triggerFileUpload}>
          <Upload size={16} />
          Upload More
        </button>
      </div>

      {/* Extraction Error */}
      {extractionError && (
        <div className="metadata-error-banner">
          <span>⚠️ {extractionError}</span>
          <button className="metadata-error-dismiss" onClick={() => setExtractionError(null)}>×</button>
        </div>
      )}

      {/* Warning Box */}
      <div className="metadata-warning-box">
        <span className="metadata-warning-icon">⚠️</span>
        <span className="metadata-warning-text">
          Metadata extraction uses the TEA AI API. Please verify extracted data for accuracy.
        </span>
      </div>
    </div>
  );

  const renderResultsState = () => {
    if (extractedMetadata.length === 0) return null;
    
    const currentDoc = extractedMetadata[currentDocumentIndex];
    const totalMetadataCount = getTotalMetadataCount();

    return (
      <div className="metadata-results-container">
        {/* Results Header */}
        <div className="metadata-results-header">
          <h2 className="metadata-summary">
            {totalMetadataCount} metadata entries extracted from {extractedMetadata.length} document{extractedMetadata.length > 1 ? 's' : ''}
          </h2>
        </div>

        {/* Document Navigation */}
        <div className="metadata-document-nav">
          <span className="metadata-doc-counter">
            Document {currentDocumentIndex + 1} of {extractedMetadata.length}
          </span>
          <h3 className="metadata-doc-title">{currentDoc.documentName}</h3>
          <div className="metadata-doc-nav-buttons">
            <button 
              className="metadata-nav-btn"
              onClick={goToPrevDocument}
              disabled={currentDocumentIndex === 0}
            >
              <ChevronLeft size={18} />
            </button>
            <button 
              className="metadata-nav-btn"
              onClick={goToNextDocument}
              disabled={currentDocumentIndex === extractedMetadata.length - 1}
            >
              <ChevronRight size={18} />
            </button>
          </div>
        </div>

        {/* Document Selector Dropdown */}
        {extractedMetadata.length > 1 && (
          <div className="metadata-doc-selector">
            <select 
              value={currentDocumentIndex}
              onChange={(e) => setCurrentDocumentIndex(Number(e.target.value))}
              className="metadata-doc-dropdown"
            >
              {extractedMetadata.map((doc, index) => (
                <option key={doc.id} value={index}>
                  {doc.documentName}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Metadata Card */}
        <div className="metadata-card">
          <div className="metadata-list-view">
            {currentDoc.metadata.map((item, index) => (
              <div key={index} className="metadata-item-row">
                <span className="metadata-item-label">{item.label}</span>
                <div className="metadata-item-value-container">
                  <span className="metadata-item-value">{item.value}</span>
                  <button 
                    className="metadata-copy-btn"
                    onClick={() => copyToClipboard(item.value, item.label)}
                    title="Copy to clipboard"
                  >
                    {copiedField === item.label ? <Check size={14} /> : <Copy size={14} />}
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* Card Actions */}
          <div className="metadata-card-actions">
            <button className="metadata-card-btn download" onClick={saveCurrentAsCSV}>
              <Download size={16} />
              Download CSV
            </button>
            <button className="metadata-card-btn save" onClick={saveCurrentAsHTML}>
              <FileDown size={16} />
              Save as HTML
            </button>
          </div>
        </div>

        {/* All Documents Actions */}
        <div className="metadata-all-actions">
          <h4 className="metadata-all-actions-title">All Documents</h4>
          <div className="metadata-all-actions-buttons">
            <button className="metadata-action-btn" onClick={saveAllAsCollatedCSV}>
              <Download size={16} />
              Save All as CSV
            </button>
            <button className="metadata-action-btn outline" onClick={saveAllAsHTML}>
              <FileDown size={16} />
              Save All as HTML
            </button>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="metadata-quick-actions">
          <button className="metadata-quick-action" onClick={triggerFileUpload}>
            <Upload size={16} /> Upload More
          </button>
          <button className="metadata-quick-action" onClick={clearAllFiles}>
            <X size={16} /> Clear All
          </button>
        </div>

        {/* Warning Box */}
        <div className="metadata-warning-box">
          <span className="metadata-warning-icon">⚠️</span>
          <span className="metadata-warning-text">
            Metadata extraction is automated. Please verify extracted data for accuracy.
          </span>
        </div>
      </div>
    );
  };

  // ============================================
  // MAIN RENDER
  // ============================================
  return (
    <div className={`metadata-extractor-revamped ${lightMode ? 'light-mode' : 'dark-mode'}`}>
      {/* Hidden file input */}
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileSelect}
        multiple
        accept=".pdf,.txt,.doc,.docx"
        style={{ display: 'none' }}
      />

      {/* Desktop Header */}
      <div className="metadata-top-header">
        <div className="metadata-header-left">
          <button className="metadata-back-btn" onClick={onBack}>
            <ChevronDown size={18} className="back-icon" />
            <span>Back to Chat</span>
          </button>
          <div className="metadata-header-info">
            <span className="metadata-header-icon">
              <Database size={20} />
            </span>
            <span className="metadata-header-name">Metadata Extractor</span>
          </div>
        </div>
        <div className="metadata-header-right">
          <span className="metadata-light-mode-label">Light mode:</span>
          <button 
            className="metadata-header-btn"
            onClick={() => setLightMode(!lightMode)}
          >
            {lightMode ? <Moon size={18} /> : <Sun size={18} />}
          </button>
        </div>
      </div>

      {/* Mobile Header */}
      <div className="metadata-mobile-header">
        <button className="metadata-mobile-menu-btn" onClick={() => setShowMobileMenu(true)}>
          <Menu size={20} />
        </button>
        <div className="metadata-mobile-title">
          <span className="metadata-mobile-icon"><Database size={20} /></span>
          <span>Metadata Extractor</span>
        </div>
        <button className="metadata-mobile-settings-btn">
          <User size={20} />
        </button>
      </div>

      {/* Main Content */}
      <div className="metadata-extractor-main" ref={scrollContainerRef}>
        {currentState === 'welcome' && renderWelcomeState()}
        {currentState === 'uploading' && renderUploadingState()}
        {currentState === 'uploaded' && renderUploadedState()}
        {currentState === 'results' && renderResultsState()}
      </div>

      {/* Mobile Menu Overlay */}
      {showMobileMenu && (
        <div className="metadata-mobile-overlay" onClick={() => setShowMobileMenu(false)}>
          <div className="metadata-mobile-menu-content" onClick={(e) => e.stopPropagation()}>
            <div className="metadata-mobile-menu-header">
              <h3>Menu</h3>
              <button className="metadata-close-menu-btn" onClick={() => setShowMobileMenu(false)}>
                <X size={18} />
              </button>
            </div>
            <div className="metadata-mobile-menu-items">
              <button className="metadata-mobile-menu-item" onClick={() => { onBack(); setShowMobileMenu(false); }}>
                <ArrowLeft size={18} /> Back to Chat
              </button>
              <button className="metadata-mobile-menu-item" onClick={() => { onNavigate('documentSearch'); setShowMobileMenu(false); }}>
                <FileText size={18} /> Document Search
              </button>
              <button className="metadata-mobile-menu-item" onClick={() => { triggerFileUpload(); setShowMobileMenu(false); }}>
                <Upload size={18} /> Upload Files
              </button>
              <button className="metadata-mobile-menu-item" onClick={clearAllFiles}>
                <X size={18} /> Clear All
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MetadataExtractorRevamped;